import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { GlobalSearch } from './GlobalSearch';
import { AgentPanel } from '../agent/AgentPanel';
// Story 2-147wg: AGENT_STATUS_EVENT/AgentStatus now come from `lib/
// agentEvents.ts` (a lower-layer, JSX-free home) rather than AgentPanel.tsx
// itself — AgentPanel.tsx no longer re-exports them (see its own comment).
import { AGENT_STATUS_EVENT, type AgentStatus } from '../../lib/agentEvents';
// Shell layout-slot primitive (story 2-127) — wired into the REAL shell here
// (story 2-128), replacing the old `sm:pl-72` fixed-offset + `main.mx-auto.
// max-w-6xl` pairing this file used to hand-roll. `AppFrame` owns rail width
// (now a SINGLE sidebar's own current width, story 2-132) and canvas
// max-width/vertical rhythm; it renders no semantic landmarks itself (its
// own header comment), so `<main>` below stays this file's own choice.
import { AppFrame } from './AppFrame';
import { registerAgentActionHandlers } from '../../lib/agentActions';
import { useCapabilities } from '../../lib/capabilities';
import { Stack, TokenBox } from '../ui';

export interface AppShellProps {
  // Layout-route usage (nested <Route element={<AppShell />}>) renders
  // <Outlet /> — the default. Story 2-45's authenticated catch-all renders
  // AppShell directly (outside the router tree it normally sits in) with
  // explicit children, so an unknown/denied route still gets nav/topbar
  // instead of the bare page a raw redirect would show.
  children?: ReactNode;
}

// Sidebar collapsed/expanded persistence (story 2-132) — plain useState +
// localStorage, following `lib/theme.tsx`'s own STORAGE_KEY pattern exactly
// (a module-level key + a plain getter read once at initial-state time), NOT
// a new React Context (the story's own explicit instruction) — AppShell owns
// the one state instance and passes `collapsed`/`onToggleCollapsed` down to
// `Sidebar` as props so the two shell surfaces that both care about it
// (`AppFrame`'s rail-reservation width via `data-shell-nav` below, and
// `Sidebar`'s own icon-only-vs-labeled rendering) can never desync.
const SIDEBAR_STORAGE_KEY = 'sitewise-sidebar-collapsed';

function getStoredCollapsed(): boolean | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return null;
}

// tokens.css's shell responsive-state contract (P10 §1.1, story 2-131):
// "tablet DEFAULTS to collapsed" — only the DEFAULT (no stored preference
// yet); an explicit stored toggle always wins at any width `sm` and up.
// Upper bound is INCLUSIVE of 1024px — `e2e/lib/app.mjs`'s own `VIEWPORTS.
// compact` fixture (1024x900) is exactly the "tablet — shell defaults to
// collapsed nav" width its own comment names; a `max-width: 1023px` query
// would exclude that exact fixture width and silently default it to
// expanded instead (found live running story-2-43 check I).
function isTabletWidth(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(min-width: 640px) and (max-width: 1024px)').matches;
}

function isMobileWidth(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 639px)').matches;
}

export function AppShell({ children }: AppShellProps = {}): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => getStoredCollapsed() ?? isTabletWidth());
  const [isMobile, setIsMobile] = useState<boolean>(() => isMobileWidth());
  const location = useLocation();
  const navigate = useNavigate();
  const caps = useCapabilities();
  const { featureState } = caps;
  // The assistant surface is itself a flagged feature (P02/P18 §2): hide it when
  // the org's AI is off or plan-locked, rather than showing a panel that only
  // refuses. 'on' → show; 'off'/'locked'/hidden → hide.
  const aiEnabled = featureState('agents.core').state === 'on';

  // Story 2-133 design-refinement pass: a THIRD independent renderer of
  // AGENT_STATUS_EVENT (joining Sidebar.tsx's ambient illumination and
  // TopBar.tsx's header dot — the exact same "N independent listeners of
  // one broadcast event" shape those two already established, not a new
  // mechanism) driving `.shell-agent-edge-glow` (tokens.css): the
  // whole-viewport edge ring. See that CSS block's own comment for why this
  // exists as a third, complementary layer rather than replacing the other
  // two.
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
  useEffect(() => {
    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<AgentStatus>).detail;
      if (detail) setAgentStatus(detail);
    };
    window.addEventListener(AGENT_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(AGENT_STATUS_EVENT, onStatus);
  }, []);

  // Below `sm` the sidebar is always the off-canvas `overlay` drawer
  // (Sidebar.tsx's own `-translate-x-full sm:translate-x-0`), independent of
  // the collapsed/expanded preference — reactive to real viewport width via
  // matchMedia, the same pattern `lib/theme.tsx` uses for
  // `prefers-color-scheme`.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  // The shell root's own `data-shell-nav` (tokens.css, story 2-131's
  // contract) — reassigns `--shell-sidebar-current-w`, which `AppFrame`'s
  // rail slot below reads directly, so the reserved canvas gutter always
  // matches whatever's actually visible: 0 while off-canvas, the collapsed
  // icon width, or the full expanded width.
  const shellNavState = isMobile ? 'overlay' : collapsed ? 'collapsed' : 'expanded';

  // Breadcrumb trail (story 2-104) used to be computed and rendered here,
  // duplicating a second, static "breadcrumb-shaped" slot TopBar rendered
  // independently just above it — reconciled to one owner by story 2-129:
  // `TopBar` now computes (via the same `resolveBreadcrumbs`) and renders
  // the real trail itself. AppShell touches neither anymore.

  // The assistant lives on every in-app page. Mounted at the shell level, its
  // conversation survives navigation between pages. (The full-screen template
  // editor sits outside the shell and mounts its own panel with live-draft
  // wiring.)
  useEffect(() => registerAgentActionHandlers({ navigate: (route) => navigate(route) }), [navigate]);
  const route = location.pathname.replace(/^\/app\/?/, '') || 'dashboard';

  // Direct user feedback (live screenshot, 2026-08-27): CapturePage's canvas
  // read as wasted-space-heavy — a wide gutter on both sides of its own
  // three-column rail/canvas/panel composition. Root cause: every shell-
  // routed page shared ONE fixed `--content-operational` (72rem) clamp here,
  // regardless of layout family — correct for a reading-width page
  // (Dashboard, Settings, an EntityListPage collection), wrong for a
  // `workspace`-family page whose OWN nested AppFrame already spends most of
  // that width on real rail/canvas/panel columns. `docs/design/sitewise-
  // ux-reference.html` — the tuned source this whole design lane cites by
  // name (2-131/2-132/2-133's own comments) — settles this directly: its
  // `.swa-frame` (the whole app shell) is `width: 100%` at desktop size, no
  // clamp at all; only PROSE content inside individual panels gets a
  // reading-width cap (`.swa-heading p { max-width: 440px }`), never the
  // shell/grid structure itself. `TemplateEditorPage.tsx` (`workspace`
  // family, `2-147f`'s own P10 §2.6 recipe) already gets this right — it
  // overrides to `var(--content-fluid)` (100%, "canvas-filling surfaces...
  // with their own internal grid", tokens.css's own definition) — but it
  // renders OUTSIDE this shell entirely, so it never needed AppShell's own
  // help. `CapturePage` is the one `workspace`-family route that IS shell-
  // routed (`modules/capture/index.ts`: `layoutFamily: 'workspace'`,
  // `chrome: 'shell'`), so it inherits the single, uniform clamp meant for
  // everything else. `/app/inspections/:id/capture` is confirmed the only
  // route matching `/capture$` in the whole route table (grepped
  // `App.tsx` + every module's own route list) — a narrow, explicit route
  // check rather than a general per-family runtime registry lookup, since
  // there is no second shell-routed workspace-family page today to justify
  // building one (the same "real second consumer, not speculative
  // machinery" discipline this lane applied to `2-147c`'s `Menu` decision
  // and `2-147i`'s agent-props-injection decision). `--content-operational`
  // referenced by its real token name here too (was the literal `"72rem"`
  // this file's own value happened to match — `2-147f`'s own grooming doc
  // already named that literal-vs-token gap as a finding for whichever
  // story next touched this file; this is that story).
  const contentMaxWidth = route.endsWith('/capture') ? 'var(--content-fluid)' : 'var(--content-operational)';

  return (
    <div className="relative min-h-screen" data-shell-nav={shellNavState}>
      {/* Story 2-133 design-refinement pass: whole-viewport ambient edge
          glow — see `.shell-agent-edge-glow`'s own header comment
          (tokens.css) for the full "why a third layer" design record.
          `aria-hidden` — purely decorative, `pointer-events: none` in its
          own CSS besides. */}
      {aiEnabled && <div className="shell-agent-edge-glow" data-agent-status={agentStatus} aria-hidden />}
      {/* Story 2-130 fix, found live while manually scroll-testing ReportDetailPage
          (the CRITICAL warning this whole UI-DS-00 lane carries — this class of bug
          is invisible to getByRole/getByTestId, only found by actually scrolling): this
          root div used to carry `overflow-hidden` itself, so it could clip the two
          decorative blur blobs below (both intentionally positioned partway outside
          its box via negative offsets). But `overflow: hidden` on ANY ancestor —
          including one whose own box always exactly equals its content height, as
          this one does (`min-h-screen` is a floor, not a cap; nothing ever actually
          overflows it) — still makes that ancestor the CSS Position spec's "nearest
          scrolling ancestor", which is what `TopBar`'s `sticky top-0` (`AppShell.tsx`
          two levels below) resolves its containing block against. Since this div's
          own box never truly needs to clip anything (it just grows to fit), sticky
          silently degraded to `relative` — invisible on every prior page in this epic
          because none had enough content to actually scroll far enough to notice.
          `ReportDetailPage`'s real rendered report content (this story) is the first
          to. Fixed by moving `overflow-hidden` OFF this root div and onto a dedicated
          `inset-0` wrapper around ONLY the two blobs below — same visual clipping,
          zero effect on TopBar's sticky containing block (now correctly the true
          viewport again). Verified by hand: scrolled a real report's rendered content
          in a live browser and watched TopBar stay pinned, not just asserted DOM
          structure. Blob size/inset now read `--shell-decor-glow-*` tokens
          (tokens.css, story 2-132) via inline style instead of the raw
          bracket-arbitrary-value geometry literals `check-shell-geometry.mjs`'s
          allowlist used to carry against this file — `-top-48`/`-bottom-40`
          stay Tailwind's own named negative-spacing scale (never flagged, no
          token needed there).

          Story 6-10: the two blobs' COLOR was, until now, also a hardcoded
          `hsl(...)` literal — `hsl(237 86% 66% / .35)` and `hsl(189 88% 68% /
          .3)` — so a tenant's brand-color override (`applyBrandingOverlay`,
          `lib/branding.ts`, stories 6-8/6-9) never reached this ambient
          chrome even though it already reaches real controls (buttons, the
          sidebar avatar gradient). Both literals numerically echoed a STOP
          inside `--gradient-primary`/`--gradient-accent` (tokens.css) rather
          than `--primary`/`--accent` themselves — but `applyBrandingOverlay`
          only ever sets `--primary`/`--primary-foreground`/`--accent`/
          `--accent-foreground` on `:root`; it does not touch either gradient
          token. Referencing the gradient stop would have kept the letter of
          "no hardcoded hex" while missing the actual point (a tenant's brand
          color visibly reaching this surface), so both blobs now read
          `hsl(var(--primary)/.35)` / `hsl(var(--accent)/.3)` directly — the
          flat, overlay-brandable tokens — at their EXISTING alpha values.
          This deliberately shifts the unbranded default hue slightly (blob 1:
          `215 84% 45%` instead of `237 85% 66%`; blob 2: `278 94% 74%`
          instead of `189 88% 68%`) — expected, not a regression: the
          near-match to the gradient stops was coincidental/historical, not
          load-bearing. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <TokenBox
          data-testid="shell-decor-glow-1"
          className="absolute -top-48 rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--primary)/.35),transparent_65%)] blur-3xl"
          right="var(--shell-decor-glow-1-inset-right)"
          height="var(--shell-decor-glow-1-size)"
          width="var(--shell-decor-glow-1-size)"
        />
        <TokenBox
          data-testid="shell-decor-glow-2"
          className="absolute -bottom-40 rounded-full bg-[radial-gradient(circle_at_center,hsl(var(--accent)/.3),transparent_65%)] blur-3xl"
          left="var(--shell-decor-glow-2-inset-left)"
          height="var(--shell-decor-glow-2-size)"
          width="var(--shell-decor-glow-2-size)"
        />
      </div>
      {/* Story 2-132: `Sidebar` is now ONE navigation tree (icon-only
          collapsed / icon+label expanded, toggled by its own header
          control) instead of the 2-127/2-128 icon-rail-plus-labeled-panel
          duo. `Sidebar`'s own root stays `fixed`-positioned (unchanged
          mechanism) so the mobile slide-in/out behavior is untouched — its
          vertical extent moved from `inset-y-0` to `bottom-0` + a `top:
          var(--shell-header-h)` offset (Sidebar.tsx's own comment) so it no
          longer starts at y=0 and visually covers TopBar's left side, since
          TopBar renders in normal flow ABOVE it, not inside the same fixed
          layer. `AppFrame`'s rail slot div still reserves the matching
          horizontal space in the flex row via `var(--shell-sidebar-
          current-w)` — the LIVE width token `data-shell-nav` (set on this
          component's own root above) drives: 0 while off-canvas
          (`overlay`), the collapsed icon width, or the full expanded width.
          Replaces `index.css`'s now-deleted `--shell-rail-w` (a single fixed
          22.5rem/0px pair that conflated the old rail+panel duo into one
          number — DEPRECATED by 2-131's own comment, removed by this story
          as instructed). */}
      {/* Real bug, found live (2026-08-23) by direct user review: `TopBar`
          used to be rendered here, as a CHILD of `AppFrame`'s `children` —
          which `AppFrame` wraps in a `py-6`/`gap-6`/`max-w-*`-clamped canvas
          column (`AppFrame.tsx`'s own inner div). A `sticky top-0` header
          nested inside that padded, gapped column visually "jumps" on scroll
          (the column's own top padding collapses out from under it the
          instant it engages) and is width-clamped to the canvas rather than
          spanning the full viewport edge-to-edge — it never covered the rail
          column at all. Fixed by lifting `TopBar` OUT of `AppFrame` entirely,
          back to a true top-level sticky bar (the exact relationship it had
          with `Sidebar` — itself `fixed inset-y-0`, unaffected either way —
          before this epic started): this flex column stacks `TopBar` first,
          full width, then `AppFrame` (rail + canvas) filling the remaining
          height below it. `<main>` stays INSIDE `AppFrame`'s children — page
          CONTENT is exactly what should be padded/width-clamped; chrome is
          not. */}
      <div className="flex min-h-screen flex-col">
        <TopBar onToggleSidebar={() => setSidebarOpen((prev) => !prev)} />
        <AppFrame
          rail={
            <Sidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
            />
          }
          railWidth="var(--shell-sidebar-current-w)"
          contentMaxWidth={contentMaxWidth}
          className="flex-1"
        >
          <Stack as="main" gap={6} className="w-full">
            {children ?? <Outlet />}
          </Stack>
        </AppFrame>
      </div>
      <GlobalSearch />
      {/* Story 2-106: the intro.js `OnboardingTour` (force-navigation route-pin
          walkthrough) is retired outright — see this story's SESSION-HANDOFF
          entry for the full graveyard sweep. Its replacement, the persisted
          first-value activation checklist, is a NON-BLOCKING widget on
          `DashboardPage` rather than a shell-level overlay: it never traps
          navigation, so it does not need to mount here. */}
      {aiEnabled && (
        // Story 2-133: shell-level agent workspace is a governed DRAWER slot
        // now, not a floating launcher+card — `variant="drawer"` renders it
        // as `.shell-agent-dock` (tokens.css), opened solely via TopBar's
        // header trigger (OPEN_AGENT_EVENT). See AgentPanel.tsx's own Props
        // comment for the full floating-vs-drawer contract.
        <AgentPanel
          agentName="assistant"
          context={{ route }}
          onArtifact={() => { /* live drafts render inside the template editor */ }}
          variant="drawer"
        />
      )}
    </div>
  );
}
